import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useShortIdContext } from '@air/provider-short-id';

import { usePublicDownloadOptions } from '~/components/AssetModalV2/hooks/usePublicDownloadOptions';
import { DownloadButtons } from '~/components/AssetModalV2/shared/components/DownloadButtons';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useDownloadPublicAsset } from '~/hooks/useDownloadPublicAsset';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { canDownloadVersion } from '~/utils/permissions/assetPermissions';

export type PublicDownloadButtonProps = {
  isIconOnly?: boolean;
};

export const PublicDownloadButton = memo(({ isIconOnly = false }: PublicDownloadButtonProps) => {
  const { shortId } = useShortIdContext();
  const clipId = useSelector(centralizedClipIdSelector);
  const { options } = usePublicDownloadOptions();
  const { permissions } = usePublicPermissions();
  const { downloadPublicAsset } = useDownloadPublicAsset();

  const canDownload = canDownloadVersion(permissions);

  const onDownload = useMemo(
    () =>
      shortId && clipId
        ? () => {
            downloadPublicAsset({
              shortId,
              clipId,
              trackLocation: 'public-asset-page',
            });
          }
        : undefined,
    [clipId, downloadPublicAsset, shortId],
  );

  if (!canDownload) {
    return null;
  }

  return <DownloadButtons isIconOnly={isIconOnly} onDownload={onDownload} options={options} />;
});

PublicDownloadButton.displayName = 'PublicDownloadButton';
